import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { BadgePercent, CalendarClock, ChevronDown, ChevronRight, Info, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { getCreditorInitials } from '@/lib/creditor';
import { isClearedDebt } from '@/lib/debtFilters';
import { debtHeadline } from '@/lib/debtHeadline';
import { scheduledPaymentDate } from '@/lib/paymentPlans';
import { formatDisplayField } from '@/lib/purchase-date';
import type { DebtData } from '@/types';
import type { DisplayField } from '@/types/core-api';
import { CurrencyDisplay } from '../ui/CurrencyDisplay';
import { PartialPaymentProgress } from '../ui/PartialPaymentProgress';
import { PaymentPlanProgress } from '../ui/PaymentPlanProgress';

interface DebtButtonProps {
  debt: DebtData;
  onClick: (debt: DebtData) => void;
  pending?: boolean;
}

/** Joined summary. A date is included only after a text field, so a bare date never reads as a due date. */
function displaySummary(fields: DisplayField[] | undefined, language: string): string {
  const parts: string[] = [];
  let seenText = false;
  for (const field of fields ?? []) {
    if (field.format === 'date' && !seenText) continue;
    if (field.format === 'text') seenText = true;
    parts.push(formatDisplayField(field, language));
  }
  return parts.join(' · ');
}

function InfoTooltip({ message }: { message: string }) {
  return (
    <span className="relative inline-flex group/tip">
      <span className="size-5 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
        <Info className="size-3" />
      </span>
      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 hidden group-hover/tip:block bg-foreground text-background text-xs rounded-md p-2 shadow-lg z-50 text-center pointer-events-none">
        {message}
      </span>
    </span>
  );
}

export function DebtButton({ debt, onClick, pending = false }: DebtButtonProps) {
  const { t, currentLanguage } = useTranslation();
  const { activeOffers } = useDebtData();
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Same predicate the Dashboard groups on, so a card never advertises an offer under a
  // heading that already calls it cleared.
  const isCompleted = isClearedDebt(debt);
  const isNegativeBalance = debt.currentBalance < 0;
  const isDisabled = isNegativeBalance || pending;
  const summary = displaySummary(debt.displayFields, currentLanguage);
  const scheduledDate = scheduledPaymentDate(debt.paymentPlan);
  // Balance, not the PAYING node: the ledger credits ~15s before the journey event moves the
  // node, so node-gating left a part-paid debt with no progress until the customer reloaded.
  const isPartPaid = debt.currentBalance > 0 && debt.originalAmount > debt.currentBalance;
  // The instalment the customer will pay next; the first one when nothing has cleared yet.
  const schedule = debt.paymentPlan?.fullPlan?.schedule;
  const nextInstalment = schedule?.find((item) => item.status !== 'succeeded') ?? schedule?.[0];
  const instalmentAmount = nextInstalment ? nextInstalment.subunits / 100 : undefined;

  // The live offer's best discount and the price it gives, shared with the chat's debt button.
  const { bestPct, amount: headlineAmount, discounted } = debtHeadline(debt, activeOffers[debt.id]);
  const discountLabel = bestPct != null ? t('discount.upToPercentOff', { pct: Math.round(bestPct * 100) }) : null;

  const plan = debt.paymentPlan;
  const showPlanProgress = !scheduledDate && !!plan?.totalPayments && plan.completedPayments !== undefined;
  // Held back while pending: a payment mid-flight would show a part-settled amount under the spinner.
  // A plan already has its instalment bar, so the paid-amount bar is for lump-sum debts only.
  const showPartialProgress = !pending && !scheduledDate && !plan && isPartPaid;
  const showProgress = showPlanProgress || showPartialProgress;
  const muted = isCompleted ? 'text-muted-foreground' : '';

  return (
    <div className="w-full stack rounded-xl bg-muted/75 overflow-hidden">
      <div
        className={`w-full rounded-xl border transition duration-300 group ${
          pending
            ? 'opacity-60'
            : isNegativeBalance
              ? 'opacity-50'
              : isCompleted
                ? 'bg-soft opacity-80 hover:bg-muted'
                : 'bg-card hover:bg-accent hover:border-ring/50'
        }`}
      >
        {/* One row at every width: mark, what it is, what it costs, where it goes */}
        <button
          type="button"
          onClick={() => !isDisabled && onClick(debt)}
          disabled={isDisabled}
          className={`flex w-full flex-wrap items-start gap-x-3 gap-y-2 p-4 text-left  ${pending ? 'cursor-wait' : isNegativeBalance ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <Avatar className="size-10 shrink-0">
            <AvatarImage src={debt.creditorLogoUrl} alt={debt.originalCreditor} />
            <AvatarFallback
              className={`text-sm font-semibold ${isCompleted ? 'bg-muted text-muted-foreground' : 'bg-accent text-accent-foreground'}`}
            >
              {getCreditorInitials(debt.originalCreditor)}
            </AvatarFallback>
          </Avatar>

          {/* Keeps a readable column; when the card is too narrow for one, the amount wraps underneath */}
          <div className="flex-1 min-w-28 stack gap-0.5">
            <h3 className={`font-medium text-base leading-tight wrap-anywhere ${muted}`}>{debt.originalCreditor}</h3>
            {summary && <p className="text-sm text-muted-foreground wrap-anywhere">{summary}</p>}
            {debt.description && <p className="text-sm text-muted-foreground wrap-anywhere">{debt.description}</p>}
            <p className="text-sm text-muted-foreground wrap-anywhere">{debt.accountNumber}</p>
          </div>

          <div className="ml-auto flex items-center gap-3 shrink-0">
            <div className="stack items-end leading-tight tabular-nums">
              {discounted && (
                <CurrencyDisplay
                  amount={debt.currentBalance}
                  currency={debt.currency}
                  className="text-xs text-muted-foreground line-through font-normal"
                />
              )}
              <CurrencyDisplay amount={headlineAmount} currency={debt.currency} className={`text-lg font-semibold tracking-tight ${muted}`} />
              {(plan || isPartPaid) && !scheduledDate && <span className="text-xs text-muted-foreground">{t('paymentPlan.remainingBalance')}</span>}
            </div>

            <span className="shrink-0 flex items-center justify-center size-5">
              {pending ? (
                <Loader2 className="size-4 text-muted-foreground animate-spin" />
              ) : isNegativeBalance ? (
                <InfoTooltip message={t('debt.contactSupport')} />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary/75 group-hover:translate-x-0.5 transition" />
              )}
            </span>
          </div>
          {(discountLabel || scheduledDate) && (
            <div className="flex flex-wrap gap-1.5 pt-1 w-full justify-center">
              {discountLabel && (
                <Badge variant="warning" shape="square" className="gap-1 max-w-full whitespace-normal text-left leading-tight">
                  <BadgePercent className="size-3 shrink-0" />
                  <span className="min-w-0">{discountLabel}</span>
                </Badge>
              )}
              {scheduledDate && (
                <Badge variant="info" shape="square" className="gap-1 max-w-full whitespace-normal text-left leading-tight">
                  <CalendarClock className="size-3 shrink-0" />
                  <span className="min-w-0">{t('dashboard.scheduledFor', { date: new Date(`${scheduledDate}T00:00:00`).toLocaleDateString() })}</span>
                </Badge>
              )}
            </div>
          )}
        </button>

        {showPlanProgress && plan && (
          <div className="px-4 pb-4">
            <PaymentPlanProgress
              totalPayments={plan.totalPayments as number}
              completedPayments={plan.completedPayments as number}
              originalAmount={debt.originalAmount}
              discount={debt.discountApplied}
              amountPaid={debt.paymentBreakdown.total}
              instalmentAmount={instalmentAmount}
              currency={debt.currency}
              showDetails={detailsOpen}
            />
          </div>
        )}
        {showPartialProgress && (
          <div className="px-4 pb-4">
            <PartialPaymentProgress
              originalAmount={debt.originalAmount}
              currentBalance={debt.currentBalance}
              currency={debt.currency}
              showDetails={detailsOpen}
            />
          </div>
        )}
      </div>

      {showProgress && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDetailsOpen((open) => !open)}
          aria-expanded={detailsOpen}
          className="w-full gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          data-testid="debt-details-toggle"
        >
          {t(detailsOpen ? 'dashboard.hideDetails' : 'dashboard.showDetails')}
          <ChevronDown className={`size-3.5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
        </Button>
      )}
    </div>
  );
}
